import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useSelector } from "../../../redux-store";
import {
  selectInitialFormData,
  selectIsOpen,
} from "../../../ducks/app/equipment-depot-dialog/selectors";
import { FormData, close } from "../../../ducks/app/equipment-depot-dialog";
import * as dataActions from "../../../ducks/data/equipment-depots";
import { formatDepotAddress } from "../../../ducks/data/equipment-depots/format";
import { AddressChange } from "../../NewPlaceSearch";
import PlaceSearchWithSavedLocations from "../../PlaceSearchWithSavedLocations/PlaceSearchWithSavedLocations";
import emptyToNull from "./empty-to-null";

const OpenEquipmentDepotDialog = ({
  initialData,
}: {
  initialData: FormData;
}) => {
  const { t } = useTranslation("equipment");
  const dispatch = useAppDispatch();

  const schema = useMemo(
    () =>
      yup.object({
        name: yup.string().required(t("depots.fields.nameRequired")),
        address: yup.string().nullable(),
        postcode: yup.string().nullable(),
        city: yup.string().nullable(),
        country: yup.string().nullable(),
        // A depot is a place a driver is sent to, so it cannot exist without
        // a position. The place search fills both coordinates in at once, so
        // each one carries the requirement itself rather than one field
        // reporting an error that belongs to the other.
        lat: yup.number().nullable().required(t("depots.fields.placeRequired")),
        lon: yup.number().nullable().required(t("depots.fields.placeRequired")),
        note: yup.string().nullable(),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: initialData,
  });
  const lat = watch("lat");
  const address = watch("address");
  const postcode = watch("postcode");
  const city = watch("city");
  const placeLine = formatDepotAddress({ address, postcode, city });
  // Both coordinates are required and are set together, so whichever of them
  // yup flagged is the one "no place picked" error.
  const placeError = errors.lat ?? errors.lon;

  // The place search owns every address field on this form: picking a place
  // fills them all in, clearing it empties them all again.
  const onPlaceSelected = useCallback(
    (place: AddressChange | null) => {
      if (!place) {
        setValue("address", null);
        setValue("postcode", null);
        setValue("city", null);
        setValue("country", null);
        setValue("lat", null);
        setValue("lon", null);
        return;
      }
      clearErrors(["lat", "lon"]);
      setValue("address", place.address || place.placeName || null);
      setValue("postcode", place.postcode || null);
      setValue("city", place.city || null);
      setValue("country", place.country || null);
      setValue("lat", place.coord.lat, { shouldValidate: true });
      setValue("lon", place.coord.lon, { shouldValidate: true });
    },
    [setValue, clearErrors]
  );

  const onSubmit = async ({ lat, lon, ...values }: FormData) => {
    // Unreachable: the schema above refuses a depot without a position. The
    // check is what narrows the nullable form field to the contract's number.
    if (lat == null || lon == null) {
      return;
    }
    const payload = {
      name: values.name,
      address: emptyToNull(values.address),
      postcode: emptyToNull(values.postcode),
      city: emptyToNull(values.city),
      country: emptyToNull(values.country),
      lat,
      lon,
      note: emptyToNull(values.note),
    };
    if (initialData.id) {
      await dispatch(
        dataActions.updateEquipmentDepot({ ...payload, id: initialData.id })
      );
    } else {
      await dispatch(dataActions.addEquipmentDepot(payload));
    }
  };

  return (
    <Dialog open onClose={() => dispatch(close())} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {initialData.id ? t("depots.editTitle") : t("depots.addTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label={t("depots.fields.name")}
              required
              {...register("name")}
              error={!!errors.name}
              helperText={errors.name?.message}
              fullWidth
            />
            <div>
              <PlaceSearchWithSavedLocations
                placeError={!!placeError}
                onPlaceSelected={onPlaceSelected}
              />
              <Typography
                variant="caption"
                color={placeError ? "error" : "text.secondary"}
                sx={{ display: "block", mt: 0.5 }}
              >
                {lat != null && placeLine
                  ? `${t("depots.fields.place")}: ${placeLine}`
                  : placeError?.message ?? ""}
              </Typography>
            </div>
            <TextField
              label={t("depots.fields.note")}
              {...register("note")}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dispatch(close())} color="inherit">
            {t("actions.cancel")}
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {t("actions.save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const EquipmentDepotDialog = () => {
  const open = useSelector(selectIsOpen);
  const initialData = useSelector(selectInitialFormData);
  if (!open || !initialData) {
    return null;
  }
  return <OpenEquipmentDepotDialog initialData={initialData} />;
};

export default EquipmentDepotDialog;
